import { Badge, Box, Flex, Text } from '@chakra-ui/react';
import { OrganizationPlatformRequirementRuleOperator } from '@client/graphql/__generated__/types';
import {
  formatPlatformRequirementForm,
  type PlatformRequirementFormReference,
} from '@common/utils/platform-requirement-presentation';
import { FC, Fragment, ReactNode } from 'react';
import {
  CHECKLIST_CHIP_TONE_STYLE,
  ChecklistChipTone,
  PRETTY_WRAP,
} from './checklistIssue.types';

/**
 * Chips for a checklist item's document classifications, shared by the expanded
 * internal card and the agent-facing hover tooltip so a form always reads as a
 * form and a category always reads as a category.
 *
 * The two are deliberately distinguishable at a glance — forms are square and
 * monospaced (they are codes), categories are rounded, bordered pills (they are
 * prose). That distinction is load-bearing whenever a requirement allows more
 * than one value, since the list mixes both kinds.
 */

/** Category values are stored screaming-snake; only ever display them spaced. */
export const formatCategory = (value: string): string =>
  value.replaceAll('_', ' ');

/**
 * The severity chip carrying a checklist verdict — on a group heading, on an
 * evidence row, or on the card header's count.
 *
 * One renderer for every surface: the same four verdicts appear on the internal
 * card and in the agent-facing evidence panel, and they were drifting apart as
 * two near-identical `Badge` copies. Only the *label* differs by surface (see
 * `getDocumentStatusBadge` vs `getRecordedVerdictBadge`), which is why the
 * label is a prop rather than derived here.
 *
 * The leading dot is what makes severity readable before the word is: a red and
 * a yellow chip at 10.5px are hard to tell apart by fill alone.
 */
export const ChecklistVerdictChip: FC<{
  label: ReactNode;
  tone: ChecklistChipTone;
  /**
   * Spoken name, for chips whose label is not self-describing — a bare count,
   * or the em dash standing in for one.
   */
  ariaLabel?: string;
}> = ({ label, tone, ariaLabel }) => {
  const style = CHECKLIST_CHIP_TONE_STYLE[tone];

  return (
    <Flex
      align="center"
      aria-label={ariaLabel}
      bg={style.bg}
      borderColor={style.border}
      borderRadius="xs"
      borderStyle={style.borderStyle}
      borderWidth={style.border ? '1px' : undefined}
      color={style.color}
      flexShrink={0}
      fontSize="10.5px"
      fontWeight="semibold"
      gap={1}
      letterSpacing="0.6px"
      lineHeight={1.4}
      px="7px"
      py="2px"
      // `aria-label` is ignored on a generic container. `img` is the role that
      // both accepts a name and suppresses the inner text, so the chip is
      // announced as "3 checklist issues" rather than as a bare "3".
      role={ariaLabel != null ? 'img' : undefined}
      textTransform="uppercase"
      whiteSpace="nowrap"
    >
      {style.dot != null ? (
        <Box bg={style.dot} borderRadius="full" boxSize="5px" flexShrink={0} />
      ) : null}
      {label}
    </Flex>
  );
};

/** Uppercase micro-label introducing a block or a value inside one. */
const RequirementLabel: FC<{ children: ReactNode; width: string }> = ({
  children,
  width,
}) => (
  <Text
    as="span"
    color="compliance.text.muted"
    flexShrink={0}
    fontSize="10.5px"
    fontWeight="semibold"
    letterSpacing="1px"
    textTransform="uppercase"
    w={width}
  >
    {children}
  </Text>
);

/**
 * One alternative per element, with the comma as its own node.
 *
 * A single joined string would be simpler, but it welds the alternatives
 * together: a long list could then only wrap mid-value, and neither a reader's
 * find-in-page nor a test can address one allowed form on its own.
 */
const RequirementValues: FC<{ values: string[] }> = ({ values }) => (
  <>
    {values.map((value, index) => (
      <Fragment key={value}>
        {index > 0 ? <Text as="span">, </Text> : null}
        <Text as="span">{value}</Text>
      </Fragment>
    ))}
  </>
);

/**
 * What would satisfy this checklist requirement, as a labelled two-column block.
 *
 * Replaces the chip row on the sidebar card. Chips distinguished a form from a
 * category by shape, which only works while the reader knows the convention; a
 * labelled row states it. It also scales better: a requirement allowing six
 * forms becomes one wrapping value instead of six chips competing with the
 * evidence below.
 *
 * The narrow agent-facing evidence panel keeps {@link ChecklistRequirementLine},
 * whose single wrapping flow survives a tooltip's width where two columns
 * would not.
 */
export const ChecklistSatisfiedBy: FC<{
  operator: OrganizationPlatformRequirementRuleOperator;
  allowedDocumentCategories: string[];
  allowedForms: PlatformRequirementFormReference[];
}> = ({ operator, allowedDocumentCategories, allowedForms }) => {
  if (allowedDocumentCategories.length === 0 && allowedForms.length === 0) {
    return null;
  }

  return (
    // "Satisfied by" heads the block on its own line. As a left-hand label
    // column it would take 74px of a 320px sidebar and leave the values —
    // which are long form names — wrapping in what remains.
    <Flex direction="column" gap="3px">
      <RequirementLabel width="auto">
        {operator === OrganizationPlatformRequirementRuleOperator.AND
          ? 'Requires all of the following'
          : 'Satisfied by'}
      </RequirementLabel>
      {allowedForms.length > 0 ? (
        <Flex align="baseline" fontSize="13.5px" gap="7px" lineHeight={1.35}>
          <RequirementLabel width="44px">Form</RequirementLabel>
          <Box
            color="compliance.text.primary"
            flex={1}
            fontWeight="medium"
            minW={0}
            sx={PRETTY_WRAP}
          >
            <RequirementValues
              values={allowedForms.map(formatPlatformRequirementForm)}
            />
          </Box>
        </Flex>
      ) : null}
      {allowedDocumentCategories.length > 0 ? (
        <Flex align="baseline" fontSize="13.5px" gap="7px" lineHeight={1.35}>
          <RequirementLabel width="44px">
            {allowedForms.length > 0
              ? operator === OrganizationPlatformRequirementRuleOperator.AND
                ? 'and Cat.'
                : 'or Cat.'
              : 'Cat.'}
          </RequirementLabel>
          <Box
            color="compliance.text.secondary"
            flex={1}
            minW={0}
            sx={PRETTY_WRAP}
          >
            <RequirementValues
              values={allowedDocumentCategories.map(formatCategory)}
            />
          </Box>
        </Flex>
      ) : null}
    </Flex>
  );
};

export const ChecklistCategoryChip: FC<{ category: string }> = ({
  category,
}) => (
  <Badge
    bg="compliance.chip.category.bg"
    border="1px solid"
    borderColor="compliance.chip.category.border"
    borderRadius="full"
    color="compliance.chip.category.text"
  >
    {formatCategory(category)}
  </Badge>
);

/**
 * A form chip rendered as `Friendly name (CODE)` when a catalog name is known,
 * otherwise the raw form code. Legacy/live callers that only have a code pass
 * `{ code, name: null }`.
 */
export const ChecklistFormChip: FC<{
  form: PlatformRequirementFormReference;
}> = ({ form }) => (
  <Badge
    bg="compliance.chip.form.bg"
    borderRadius="sm"
    color="compliance.chip.form.text"
    fontFamily="mono"
    maxW="100%"
    overflowWrap="anywhere"
    whiteSpace="normal"
  >
    {formatPlatformRequirementForm(form)}
  </Badge>
);

/**
 * The "Needs at least one attachment classified with: …" requirement, as a
 * single wrapping flow of label + chips.
 *
 * One `wrap` container rather than a label above a separate chip row: a
 * requirement can allow many values, and they have to spill onto following
 * lines rather than clip or force the tooltip to scroll sideways.
 *
 * Renders nothing when neither categories nor forms are known — an empty
 * requirement box says nothing worth the space.
 */
export const ChecklistRequirementLine: FC<{
  operator: OrganizationPlatformRequirementRuleOperator;
  allowedDocumentCategories: string[];
  allowedForms: PlatformRequirementFormReference[];
}> = ({ operator, allowedDocumentCategories, allowedForms }) => {
  if (allowedDocumentCategories.length === 0 && allowedForms.length === 0) {
    return null;
  }

  return (
    <Flex align="center" gap={2} wrap="wrap">
      <Text as="span" color="compliance.text.secondary" fontSize="sm">
        {operator === OrganizationPlatformRequirementRuleOperator.AND
          ? 'Requires all of the following classifications:'
          : 'Needs at least one attachment classified with:'}
      </Text>
      {allowedDocumentCategories.map((category) => (
        <ChecklistCategoryChip
          key={`category-${category}`}
          category={category}
        />
      ))}
      {allowedForms.length > 0 && allowedDocumentCategories.length > 0 ? (
        <Text
          as="span"
          color="compliance.text.muted"
          fontSize="xs"
          fontStyle="italic"
        >
          {operator === OrganizationPlatformRequirementRuleOperator.AND
            ? 'and'
            : 'or'}
        </Text>
      ) : null}
      {allowedForms.map((form) => (
        <ChecklistFormChip key={`form-${form.code}`} form={form} />
      ))}
    </Flex>
  );
};
